import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Button, ButtonLink, Price } from '@artosera/ui';
import {
  useCreerLienPaiement,
  useEnvoyerLienPaiement,
  useFacturePaiement,
  useRevoquerLienPaiement,
} from '../../api/paiementEnLigne.js';
import type { GalerieSession } from '../../api/types.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import {
  EncaissementsPaiement,
  ErreurPaiement,
  HistoriqueRelances,
  SuspensionPaiement,
  TentativesPaiement,
  useTextePaiement,
} from './paiementUi.js';
import styles from './PaiementEnLigne.module.css';

export function PaiementEnLigneSection(props: {
  galerieSlug: string;
  galerie: GalerieSession;
  factureId: string;
  type: string;
  etat: string;
}) {
  if (
    !galerieAModule(props.galerie, 'paiement-en-ligne') ||
    !galerieALaction(props.galerie, 'online-payments:read') ||
    props.type !== 'facture' ||
    props.etat !== 'emise'
  )
    return null;
  return <Contenu {...props} />;
}
function Contenu({
  galerieSlug,
  galerie,
  factureId,
}: {
  galerieSlug: string;
  galerie: GalerieSession;
  factureId: string;
}) {
  const { t, label, langue } = useTextePaiement();
  const lecture = useFacturePaiement(galerieSlug, factureId);
  const creer = useCreerLienPaiement(galerieSlug);
  const revoquer = useRevoquerLienPaiement(galerieSlug);
  const envoyer = useEnvoyerLienPaiement(galerieSlug);
  const [copie, setCopie] = useState(false);
  const [erreurCopie, setErreurCopie] = useState<unknown>(null);
  const peutEcrire = galerieALaction(galerie, 'online-payments:write');
  return (
    <section className={styles.section}>
      <h2>{label('sectionTitre')}</h2>
      {lecture.isLoading && <p>{t('commun:chargement')}</p>}
      <ErreurPaiement erreur={lecture.error} />
      {lecture.data && (
        <>
          <p>
            {label('resteDu')} : <Price cents={lecture.data.resteDuCentimes} lang={langue} />
          </p>
          {peutEcrire && (
            <div className={styles.actions}>
              <Button
                loading={creer.isPending}
                onClick={() => {
                  setCopie(false);
                  envoyer.reset();
                  creer.mutate(factureId);
                }}
              >
                {label('creerLien')}
              </Button>
              {lecture.data.lienUrl && (
                <>
                  <ButtonLink
                    variant="secondary"
                    href={lecture.data.lienUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink size={16} strokeWidth={1.5} aria-hidden />
                    {label('ouvrirLien')}
                  </ButtonLink>
                  <Button
                    onClick={() => {
                      setErreurCopie(null);
                      void navigator.clipboard
                        .writeText(lecture.data.lienUrl!)
                        .then(() => setCopie(true))
                        .catch(setErreurCopie);
                    }}
                  >
                    {label('copier')}
                  </Button>
                  <Button loading={envoyer.isPending} onClick={() => envoyer.mutate(factureId)}>
                    {label('envoyerEmail')}
                  </Button>
                  <Button
                    loading={revoquer.isPending}
                    onClick={() => {
                      setCopie(false);
                      revoquer.mutate(factureId);
                    }}
                  >
                    {label('revoquer')}
                  </Button>
                </>
              )}
            </div>
          )}
          {copie && <p role="status">{label('copie')}</p>}
          {envoyer.data && <p role="status">{label(`etats.${envoyer.data.resultat}`)}</p>}
          <ErreurPaiement erreur={creer.error ?? revoquer.error ?? envoyer.error ?? erreurCopie} />
          <ul>
            {lecture.data.liens.map((l) => (
              <li key={l.id}>
                {label(`etats.${l.etat}`)} · {label('expiration')} {l.expireLe.slice(0, 10)}
                <ul>
                  {l.envois.map((envoi, i) => (
                    <li key={i}>
                      {envoi.le.slice(0, 10)} · {envoi.destinataire} ·{' '}
                      {label(`etats.${envoi.resultat}`)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <p>{label('captureExplication')}</p>
          <TentativesPaiement tentatives={lecture.data.tentatives} />
          <section>
            <h3>{label('encaissements')}</h3>
            <EncaissementsPaiement
              galerieSlug={galerieSlug}
              encaissements={lecture.data.encaissements}
            />
          </section>
          <section>
            <h3>{label('relances')}</h3>
            <HistoriqueRelances galerieSlug={galerieSlug} relances={lecture.data.relances} />
            {lecture.data.suivi?.relancesSuspendues && (
              <p>
                {label('suspendues')} · {lecture.data.suivi.motifSuspension}
              </p>
            )}
            {peutEcrire && (
              <SuspensionPaiement
                galerieSlug={galerieSlug}
                factureId={factureId}
                suspendues={lecture.data.suivi?.relancesSuspendues ?? false}
              />
            )}
          </section>
        </>
      )}
    </section>
  );
}
